if (!customElements.get('theme-swiper')) {
  class ThemeSwiper extends HTMLElement {
    constructor() {
      super();
      this._onVideoEnded = null;
      this.lastOutIndex = null;
      this.swiper = null;
      this.breakpointList = ['small', 'medium', 'large-up'];
      this.boundPlaySlider = this.playSlider.bind(this);
      this.boundPauseSlider = this.pauseSlider.bind(this);
    }

    get threshold() {
      return Number(this.getAttribute('data-threshold') ?? -200)
    }

    get container() {
      return this._container = this._container || this.querySelector('[data-swiper-container]') || this.querySelector('.swiper')
    }

    get wrapper() {
      return this._wrapper = this._wrapper || this.querySelector('[data-swiper-wrapper]') || this.querySelector('.swiper-wrapper')
    }

    get scrollbar() {
      return  this.querySelector(':scope > .swiper-scrollbar')
    }

    get pagination() {
      return this.querySelector(`#Pagination-Slider-${this.dataset.sliderId}`) || this.querySelector(':scope > .swiper-pagination')
    }

    get prev() {
      return  this.querySelector(`#Navigation-Slider-${this.dataset.sliderId} .swiper-button-prev`) || this.querySelector(':scope > .swiper-button-prev')
    }
    
    get next() {
      return  this.querySelector(`#Navigation-Slider-${this.dataset.sliderId} .swiper-button-next`) || this.querySelector(':scope > .swiper-button-next')
    }

    get playButton() {
      return this.querySelector('.swiper-button-play')
    }

    get pauseButton() {
      return this.querySelector('.swiper-button-pause')
    }

    get resolution() {
      return this.getAttribute('data-resolution')
    }

    get autoplayDelayTime() {
      return parseInt(this.getAttribute('data-autoplay-delay'))
    }

    get items() {
      if (!this._items) {
        let items = Array.from(this.querySelectorAll('[data-swiper-slide]'));
        if (items.length === 0) {
          items = Array.from(this.querySelectorAll('.swiper-slide'));
        }
        this._items = items;
      }
      return this._items
    }

    get markedItem() {
      return this._markedItem = this._markedItem || this.querySelector('.swiper-slide.swiper-slide-active [data-marked]');
    }

    convertBreakpoint(breakpoint) {
      let screen;
      switch (breakpoint) {
        case 'small':
          screen = '(max-width: 749px)';
          break;
        case 'medium':
          screen = '(min-width: 750px) and (max-width: 1032px)';
          break;
        case 'large-up':
          screen = '(min-width: 1033px)';
          break;
      }

      return screen
    }

    createObserver(options) {
      const threshold = options.threshold ? options.threshold : 0;
      const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (typeof options.callback === 'function') {
              options.callback();
              observer.unobserve(entry.target);
            }
          }
        });
      }, { rootMargin: `0px 0px ${threshold}px 0px` });
      observer.observe(options.element);
    }

    connectedCallback() {
      if (!this.getAttribute('loaded')) {
        this.createObserver({
          element: this,
          callback: this.init.bind(this),
          threshold: this.threshold
        });
      } else {
        this.init();
      }
      this.playButton?.addEventListener('click', this.boundPlaySlider);
      this.pauseButton?.addEventListener('click', this.boundPauseSlider);

      if (this.resolution) {
        const mq = window.matchMedia(this.resolution);
        this._resolutionListener = () => this.init();
        mq.addEventListener('change', this._resolutionListener);
      }
    }

    disconnectedCallback() {
      this.playButton?.removeEventListener('click', this.boundPlaySlider);
      this.pauseButton?.removeEventListener('click', this.boundPauseSlider);

      if (this.resolution && this._resolutionListener) {
        const mq = window.matchMedia(this.resolution);
        mq.removeEventListener('change', this._resolutionListener);
        this._resolutionListener = null;
      }
    }

    init() {
      this.options = this.swiperOptions();
      if(this.resolution != undefined) {
        if (window.matchMedia(this.resolution).matches) {
          if (!this.swiper) this.swiperInitilize();
        } else {
          if (this.swiper) this.swiperDestroy();
        }
      } else {
        if (!this.swiper) this.swiperInitilize();
      }
    }

    bindEvents() {
      if (!this.swiper) return;
    
      this._onSlideChange = () => {
        this.swiper.pagination.render();
        this.swiper.pagination.update();
        this.swiper.navigation.destroy();
        this.swiper.navigation.init();
        this.dispatchEvent(new CustomEvent('slider:change', {
          bubbles: true,
          detail: { currentPage: this.swiper.activeIndex }
        }));
      };
    
      this._onTransitionStart = () => {
        const oldIndex = this.swiper.previousIndex !== this.swiper.activeIndex
          ? this.swiper.previousIndex
          : this.lastOutIndex;
        const oldSlide = this.swiper.slides[oldIndex];
        if (oldSlide?.dataset.type === 'video') this.handleVideoOut(oldSlide, this.swiper);
        this.swiper.mousewheel.enabled = false;
        this.lastOutIndex = this.swiper.previousIndex;
      };
    
      this._onTransitionEnd = () => {
        const newSlide = this.swiper.slides[this.swiper.activeIndex];
        if (newSlide?.dataset.type === 'video') this.handleVideoIn(newSlide, this.swiper);
        this.swiper.mousewheel.enabled = true;
        this.lastOutIndex = this.swiper.activeIndex;
        this.setSlideEdges(this.swiper);
      };
    
      this._onPaginationUpdate = () => this.updatePagination(this.swiper);
      this._onSlideChangeTransitionEnd = () => this.handleLoop(this.swiper);
      this._onAutoplayPause = () => this.setAutoplayState(this.swiper);
      this._onAutoplayResume = () => this.setAutoplayState(this.swiper);
      this._onAutoplayStop = () => this.setAutoplayState(this.swiper);
      this._onAutoplayStart = () => this.setAutoplayState(this.swiper);

      this.swiper.on('slideChange', this._onSlideChange);
      this.swiper.on('transitionStart', this._onTransitionStart);
      this.swiper.on('transitionEnd', this._onTransitionEnd);
      this.swiper.on('paginationUpdate', this._onPaginationUpdate);
      this.swiper.on('slideChangeTransitionEnd', this._onSlideChangeTransitionEnd);
      this.swiper.on('autoplayPause', this._onAutoplayPause);
      this.swiper.on('autoplayResume', this._onAutoplayResume);
      this.swiper.on('autoplayStart', this._onAutoplayStart);
      this.swiper.on('autoplayStop', this._onAutoplayStop);

      if (Shopify.designMode) {
        const section = this.closest('.shopify-section');
        this._onBlockSelect = this.onBlockSelect.bind(this);
        this._onSectionSelect = this.onSectionSelect.bind(this);
        section.addEventListener('shopify:section:load', this._onSectionSelect);
        section.addEventListener('shopify:section:unload', this._onSectionSelect);
        section.addEventListener('shopify:section:reorder', this._onSectionSelect);
        this.addEventListener('shopify:block:select', this._onBlockSelect);
      }
    }
    
    unBindEvents() {
      if (!this.swiper) return;
    
      this.swiper.off('slideChange', this._onSlideChange);
      this.swiper.off('transitionStart', this._onTransitionStart);
      this.swiper.off('transitionEnd', this._onTransitionEnd);
      this.swiper.off('paginationUpdate', this._onPaginationUpdate);
      this.swiper.off('slideChangeTransitionEnd', this._onSlideChangeTransitionEnd);
      this.swiper.off('autoplayPause', this._onAutoplayPause);
      this.swiper.off('autoplayResume', this._onAutoplayResume);
      this.swiper.off('autoplayStart', this._onAutoplayStart);
      this.swiper.off('autoplayStop', this._onAutoplayStop);
    
      this._onSlideChange = null;
      this._onTransitionStart = null;
      this._onTransitionEnd = null;
      this._onPaginationUpdate = null;
      this._onSlideChangeTransitionEnd = null;
      this._onAutoplayPause = null;
      this._onAutoplayResume = null;
      this._onAutoplayStop = null;
      this._onAutoplayStart = null;
      this.lastOutIndex = null;

      if (Shopify.designMode) {
        if(this._onBlockSelect) {
          this.removeEventListener('shopify:block:select', this._onBlockSelect);
          this._onBlockSelect = null;
        }

        if(this._onSectionSelect) {
          const section = this.closest('.shopify-section');
          section.removeEventListener('shopify:section:load', this._onSectionSelect);
          section.removeEventListener('shopify:section:unload', this._onSectionSelect);
          section.removeEventListener('shopify:section:reorder', this._onSectionSelect);
          this._onSectionSelect = null;
        }
      }
    }

    swiperOptions() {
      let defaultOptions = {};
      try {
        defaultOptions = JSON.parse(this.dataset.swiper || '{}');
      } catch (e) {
        console.error('Invalid JSON in data-swiper:', e);
      }
      let navigation = {
        prevEl: this.prev,
        nextEl: this.next
      };
      let pagination = {};
      if(this.pagination != undefined) {
        let isFraction = this.pagination.classList.contains('fraction');
        if (isFraction) {
          pagination = {
            el: this.pagination,
            type: 'fraction',
            renderFraction: (currentClass, totalClass) => `
              <span class="${currentClass}"></span>
              <span class="swiper-pagination-separator"> / </span>
              <span class="${totalClass}"></span>
            `
          };
        } else {
          pagination = {
            el: this.pagination,
            clickable: true,
            renderBullet: (index, className) => {
              return `<span class="${className}">${index + 1}</span>`;
            }
          };
        }
      }
      let options = { ...defaultOptions };
      options = {
        ...options,
        loop: options.loop ?? false,
        pagination: {
          ...pagination
        },
        navigation: {
          ...navigation
        },
        scrollbar: {
          el: this.scrollbar,
          hide: false
        },
        a11y: {
          enabled: true
        },
        on: {
          init: (element) => {
            const selectedElement = element.slides[element.activeIndex];
            if (selectedElement && selectedElement.dataset.type === 'video') this.handleVideoIn(selectedElement, element);
            const indexInit = this.indexInit || this.dataset.activeIndexInit;
            indexInit && this.setActiveSlideItem(indexInit);
            this.setSlideHeight();
            this.setSlideEdges(element);
            this.checkNavigation(element);
            this.checkPagination(element);
            this.updatePagination(element);
            element.slides.forEach((slide, index) => slide.setAttribute('data-index', index));
          },
          resize: (element) => {
            this.setSlideHeight();
            this.setSlideEdges(element);
            this.checkNavigation(element);
            this.checkPagination(element);
          },
          update: (element) => {
            this.setSlideHeight();
            this.setSlideEdges(element);
            this.checkNavigation(element);
            this.checkPagination(element);
          }
        }
      }

      return options
    }

    swiperInitilize(){
      if(!this.container.classList.contains('swiper')) this.container.classList.add('swiper');
      if(!this.wrapper.classList.contains('swiper-wrapper')) this.wrapper.classList.add('swiper-wrapper');
      this.querySelectorAll('[data-swiper-slide]').forEach((element) => {
        if(!element.classList.contains('swiper-slide')) element.classList.add('swiper-slide')
      });
      this.swiper = new Swiper(this.container, this.options);
      this.dispatchEvent(new CustomEvent('slider:init', {
        bubbles: true,
        detail: {
          id: this.dataset.sliderId,
          swiper: this.swiper
        }
      }));
      this.bindEvents();
    }

    swiperDestroy() {
      this.unBindEvents();
      this.swiper?.destroy(true, true);
      this.swiper = null;
      if(this.container.classList.contains('swiper')) this.container.classList.remove('swiper');
      if(this.wrapper.classList.contains('swiper-wrapper')) this.wrapper.classList.remove('swiper-wrapper');
      this.querySelectorAll('[data-swiper-slide]').forEach((element) => {
        if(element.classList.contains('swiper-slide')) element.classList.remove('swiper-slide')
      });
      this.dispatchEvent(new CustomEvent('slider:destroy', {
        bubbles: true,
        detail: {
          id: this.dataset.sliderId
        }
      }));
    }

    setActiveSlideItem(activeIndex) {
      if (typeof activeIndex === 'undefined' || activeIndex === null) return;
      const idx = Number(activeIndex);
      if (Number.isFinite(idx)) {
        this.swiper?.params?.loop ? this.swiper.slideToLoop(idx) : this.swiper?.slideTo(idx);
      }
    }

    setSlideHeight() {
      if (!this.markedItem) return;
      this.style.setProperty('--slide-height', `${this.markedItem.offsetHeight}px`);
    }

    setSlideEdges(swiper) {
      const isBeginning = swiper.isBeginning;
      const isEnd = swiper.isEnd;
      swiper.el.classList.toggle('show-left', !isBeginning);
      swiper.el.classList.toggle('show-right', !isEnd);
    }

    setAutoplayState(swiper) {
      if(!swiper?.autoplay) return;
      const isStopped = swiper.autoplay.running === false;
      const isPaused  = swiper.autoplay.paused === true && !isStopped;
      this.classList.toggle('theme-swiper--stopped', isStopped);
      this.classList.toggle('theme-swiper--paused',  isPaused);
    }

    handleLoop(swiper) {
      if (swiper && swiper.params.loop) swiper.loopFix();
    }

    handleVideoOut(oldElement, swiper) {
      if (!oldElement) return;
      const videoElement = window.visibleMedia(oldElement.querySelectorAll('video-media'));
      if (!videoElement) return;
      videoElement.pause();
      videoElement.updateControlButton(true);
      videoElement.updateSoundButton(true);
      if (this._onVideoEnded) {
        videoElement.removeEventListener('video:ended', this._onVideoEnded);
        this._onVideoEnded = null;
      }
    }

    handleVideoIn(newElement, swiper) {
      if (!newElement) return;
      const videoElement = window.visibleMedia(newElement.querySelectorAll('video-media'));
      if (!videoElement) return;
      if (swiper.params.autoplay.enabled && swiper.autoplay.running) {
        // swiper.params.autoplay.delay = 0;
        swiper.autoplay.stop();
      }
      videoElement.play();
      videoElement.updateControlButton(false);
      videoElement.updateSoundButton(false);
      const handleVideoEnd = () => {
        this._onVideoEnded = null;
        videoElement.removeEventListener('video:ended', handleVideoEnd);
        if (swiper.params.autoplay.enabled && !swiper.autoplay.running) {
          swiper.autoplay.start();
          //  swiper.params.autoplay.delay = this.autoplayDelayTime;
          // swiper.slideNext();
        }
      };
      this._onVideoEnded = handleVideoEnd;
      videoElement.addEventListener('video:ended', this._onVideoEnded);
    }

    handleTabindex(swiper) {
      const focusableSelectors = 'a, button, input, textarea, select, [tabindex]';
      const totalSlides = swiper.slides.length;
      const slidesPerView = swiper.params.slidesPerView;
      if (swiper && totalSlides > slidesPerView) {
        swiper.slides.forEach((slide) => {
          slide.querySelectorAll(focusableSelectors).forEach((element) => element.setAttribute('tabindex', '-1'));
        });
        swiper.slides[swiper.activeIndex]?.querySelectorAll(focusableSelectors).forEach((element) => element.setAttribute('tabindex', element.hasAttribute('data-omit-tabindex') ? '-1' : '0'));
      } else {
        swiper.slides.forEach((slide) => {
          slide.querySelectorAll(focusableSelectors).forEach((element) => element.setAttribute('tabindex', element.hasAttribute('data-omit-tabindex') ? '-1' : '0'));
        });
      }
    }

    handleKeyboard(event) {
      const keyCode = event.keyCode || event.which;
      const focusedElement = document.activeElement;
      if (this.swiper && this.swiper.el.contains(focusedElement)) {
        switch (keyCode) {
          case 37:
            this.swiper.slidePrev();
            break;
          case 39:
            this.swiper.slideNext();
            break;
        }
      }
    }

    playSlider(event) {
      this.togglePlayPause(event.currentTarget.name);
      if(this.swiper) {
        this.swiper.params.autoplay.delay = this.autoplayDelayTime;
        this.swiper.autoplay.start();
      }
      this.classList.remove('theme-swiper--paused');
    }

    pauseSlider(event) {
      this.togglePlayPause(event.currentTarget.name);
      if(this.swiper) {
        this.swiper.params.autoplay.delay = 0;
        this.swiper.autoplay.stop();
      }
      this.classList.add('theme-swiper--paused');
    }

    togglePlayPause(state = 'play') {
      if(state == 'play') {
        this.playButton?.classList.add('hidden');
        this.pauseButton?.classList.remove('hidden');
      } else if(state == 'pause') {
        this.pauseButton?.classList.add('hidden');
        this.playButton?.classList.remove('hidden');
      }
    }

    updatePagination(swiper) {
      if (!swiper) return;
      if (swiper.params?.pagination?.type !== 'bullets') return;
      const cloneSlides = swiper.el.querySelectorAll('.swiper-slide-duplicate, .swiper-slide--cloned');
      if (cloneSlides.length === 0) return;
      const bullets = swiper.pagination.bullets;
      if (bullets.length <= 1) return;
      const index = swiper.realIndex - 1;
      const halfLength = Math.floor(bullets.length / 2);
      if (index >= halfLength - 1) {
        for (let i = 0; i < halfLength; i++){
          bullets[i].style.display = 'none';
        }
      } else {
        for (let i = halfLength; i < bullets.length; i++) {
          bullets[i].style.display = 'none';
        }
      }
    }

    checkPagination(swiper) {
      let pagination = swiper.pagination;
      if (!pagination.el || pagination.bullets.length == 1) return;
      let breakpoint = [...this.breakpointList].find((breakpoint) => window.matchMedia(this.convertBreakpoint(breakpoint)).matches);
      this.classList.toggle('has-pagination', !pagination.el.classList.contains(`${breakpoint}-hide`));
    }

    checkNavigation(swiper) {
      let navigation = swiper.navigation;
      if (!navigation?.nextEl && !navigation?.prevEl) return;
      const nextDisabled = navigation.nextEl?.hasAttribute('disabled');
      const prevDisabled = navigation.prevEl?.hasAttribute('disabled');
      if (nextDisabled && prevDisabled) return;
      let breakpoint = [...this.breakpointList].find((breakpoint) => window.matchMedia(this.convertBreakpoint(breakpoint)).matches);
      this.classList.toggle('has-navigation', !navigation.nextEl.closest('.swiper-navigation').classList.contains(`${breakpoint}-hide`));
    }

    onSectionSelect(event) {
      const activeItem = event.target.querySelector('.swiper-slide-active');
      if(!activeItem) return;
      // if(!this.indexInit) this.indexInit = Number(activeItem.dataset.index);
      this.setActiveSlideItem(activeItem.dataset.index)
    }

    onBlockSelect(event) {
      // if(!this.indexInit) this.indexInit =  Number(event.target.dataset.index);
      this.setActiveSlideItem(event.target.dataset.index)
    }
  }

  customElements.define('theme-swiper', ThemeSwiper);
}